import { useState, useEffect } from 'react';
import { Container, Title, Grid, Card, Text, Group, Progress, Badge, Stack, Button } from '@mantine/core';
import { IconMessageCircle, IconTrendingUp, IconZoom, IconRefresh } from '@tabler/icons-react';

interface SessionStats {
  totalMessages: number;
  userMessages: number;
  aiMessages: number;
  averageConfidence: number;
  totalSources: number;
  lastUpdated: Date;
}

export default function ConsoleStatsPage() {
  const [stats, setStats] = useState<SessionStats>({
    totalMessages: 0,
    userMessages: 0,
    aiMessages: 0,
    averageConfidence: 0,
    totalSources: 0,
    lastUpdated: new Date(),
  });

  // Load stats from localStorage (shared with Console)
  const loadStats = () => {
    const stored = localStorage.getItem('consoleStats');
    if (stored) {
      const parsed = JSON.parse(stored);
      setStats({
        ...parsed,
        lastUpdated: new Date(parsed.lastUpdated),
      });
    }
  };

  useEffect(() => {
    loadStats();
    // Refresh every 5 seconds to see live updates
    const interval = setInterval(loadStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const confidencePercent = Math.round(stats.averageConfidence * 100);
  const avgSourcesPerResponse = stats.aiMessages > 0 ? (stats.totalSources / stats.aiMessages).toFixed(1) : '0';

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={1}>Console Statistics Dashboard</Title>
              <Text c="dimmed" size="sm" mt="xs">
                Real-time metrics from your Eclipse AI conversations
              </Text>
            </div>
            <Group gap="xs">
              <Badge color="green" variant="filled">
                🟢 Live
              </Badge>
              <Button
                size="xs"
                variant="light"
                onClick={loadStats}
                leftSection={<IconRefresh size={14} />}
              >
                Refresh
              </Button>
            </Group>
          </Group>
          <Text size="xs" c="dimmed" mt="md">
            Last updated: {stats.lastUpdated.toLocaleTimeString()}
          </Text>
        </div>

        {/* Main Stats Grid */}
        <Grid>
          {/* Total Messages */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Total Messages
                </Text>
                <Badge color="blue" variant="light">
                  <IconMessageCircle size={14} />
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {stats.totalMessages}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                {stats.userMessages} questions • {stats.aiMessages} responses
              </Text>
            </Card>
          </Grid.Col>

          {/* Average Confidence */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Avg Confidence
                </Text>
                <Badge color="green" variant="light">
                  <IconTrendingUp size={14} />
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {confidencePercent}%
              </Text>
              <Progress value={confidencePercent} size="md" color="green" mt="md" />
              <Text size="xs" c="dimmed" mt="xs">
                AI response quality indicator
              </Text>
            </Card>
          </Grid.Col>

          {/* Sources Retrieved */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Sources Retrieved
                </Text>
                <Badge color="orange" variant="light">
                  <IconZoom size={14} />
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {stats.totalSources}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                From Kenbri Group knowledge base
              </Text>
            </Card>
          </Grid.Col>

          {/* Avg per Response */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Sources/Response
                </Text>
                <Badge color="violet" variant="light">
                  📊
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {avgSourcesPerResponse}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Average research depth
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Detail Cards */}
        <Grid>
          {/* User Engagement */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder padding="lg" radius="md">
              <Text fw={600} mb="md">
                User Engagement
              </Text>
              <Stack gap="md">
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Questions Asked</Text>
                    <Text fw={600} size="sm">
                      {stats.userMessages}
                    </Text>
                  </Group>
                  <Progress
                    value={(stats.userMessages / Math.max(stats.totalMessages, 1)) * 100}
                    color="blue"
                    size="sm"
                  />
                </div>
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">AI Responses</Text>
                    <Text fw={600} size="sm">
                      {stats.aiMessages}
                    </Text>
                  </Group>
                  <Progress
                    value={(stats.aiMessages / Math.max(stats.totalMessages, 1)) * 100}
                    color="green"
                    size="sm"
                  />
                </div>
              </Stack>
            </Card>
          </Grid.Col>

          {/* Quality Metrics */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder padding="lg" radius="md">
              <Text fw={600} mb="md">
                Quality Metrics
              </Text>
              <Stack gap="md">
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Response Confidence</Text>
                    <Badge color="green">{confidencePercent}%</Badge>
                  </Group>
                  <Progress value={confidencePercent} color="green" size="sm" />
                </div>
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Research Depth</Text>
                    <Text fw={600} size="sm">
                      {avgSourcesPerResponse} sources/response
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Higher values indicate more thorough research
                  </Text>
                </div>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Info Box */}
        <Card withBorder padding="md" radius="md" style={{ background: '#f0f9ff' }}>
          <Group gap="xs">
            <Text size="sm" style={{ flex: 1 }}>
              💡 <strong>Tip:</strong> Stats update in real-time as you use the Console. Open the Console to start a
              conversation and watch the metrics update!
            </Text>
          </Group>
        </Card>
      </Stack>
    </Container>
  );
}
